import React, { useState, useEffect } from 'react';
import { Page } from '../types';

interface HeaderProps {
  currentPage: Page;
  navigate: (page: Page) => void;
  highContrast: boolean;
  setHighContrast: (val: boolean) => void;
  fontSize: number;
  setFontSize: (val: number) => void;
}

const Logo: React.FC<{ highContrast: boolean }> = ({ highContrast }) => (
  <div className="flex items-center space-x-3 group cursor-pointer">
    <div className="relative w-12 h-12 flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
      <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-sm" aria-hidden="true">
        <path d="M35 25 C35 15, 65 15, 65 25" fill="none" stroke="#ee223b" strokeWidth="8" strokeLinecap="round" />
        <rect x="15" y="30" width="70" height="60" rx="20" fill="#ee223b" />
        <rect x="44" y="45" width="12" height="30" rx="3" fill="white" />
        <rect x="35" y="54" width="30" height="12" rx="3" fill="white" />
      </svg>
    </div>
    <div className="flex flex-col md:flex-row md:items-baseline">
      <span className={`text-2xl font-extrabold tracking-tighter ${highContrast ? 'text-yellow-400' : 'text-secondary'}`}>
        ErsteHilfe
      </span>
      <span className={`text-2xl font-black tracking-tighter ${highContrast ? 'text-white underline' : 'text-primary'}`}>2Go</span>
    </div>
  </div>
);

const Header: React.FC<HeaderProps> = ({ 
  currentPage, 
  navigate, 
  highContrast, 
  setHighContrast,
  // fontSize,
  // setFontSize
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // const [showAccess, setShowAccess] = useState(false);

  // PERFORMANCE: Predictive Prefetching Helper
  const prefetchPage = (page: Page) => {
    switch (page) {
      case 'ueber-uns': import('../pages/AboutPage'); break;
      case 'funktionen': import('../pages/FeaturesPage'); break;
      case 'faq': import('../pages/FAQPage'); break;
      case 'status': import('../pages/StatusPage'); break;
      case 'wissenshub': import('../pages/KnowledgeHubPage'); break;
      // case 'mitarbeiten': import('../pages/CollaboratePage'); break;
      case 'blog': import('../pages/BlogPage'); break;
      case 'warteliste': import('../pages/DownloadPage'); break;
    }
  };

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems: { name: string, page: Page }[] = [
    { name: 'Start', page: 'start' },
    { name: 'Über uns', page: 'ueber-uns' },
    { name: 'Funktionen', page: 'funktionen' },
    { name: 'Status', page: 'status' },
    { name: 'WissensHub', page: 'wissenshub' },
    // { name: 'Mitarbeiten', page: 'mitarbeiten' },
    { name: 'Blog', page: 'blog' },
    { name: 'FAQ', page: 'faq' },
  ];

  const handleNav = (page: Page) => {
    navigate(page);
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (currentPage === 'start') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setMobileMenuOpen(false);
      return;
    }

    handleNav('start');
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || currentPage !== 'start' ? (highContrast ? 'bg-black border-b-2 border-yellow-400' : 'bg-white shadow-md') : 'bg-transparent'} py-3`}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        <button 
          onClick={handleLogoClick}
          className={`outline-none focus:ring-4 rounded-xl transition-shadow ${highContrast ? 'focus:ring-yellow-400' : 'focus:ring-primary/20'}`}
          aria-label="ErsteHilfe2Go - Zur Startseite"
        >
          <Logo highContrast={highContrast} />
        </button>

        <nav className="hidden lg:flex items-center space-x-6" aria-label="Hauptnavigation">
          {navItems.map((item) => (
            <button 
              key={item.name} 
              onClick={() => handleNav(item.page)} 
              onMouseEnter={() => prefetchPage(item.page)} // PERFORMANCE: Prefetch on hover
              className={`font-semibold text-sm transition-colors focus:outline-none focus:ring-2 p-1 rounded ${
                currentPage === item.page 
                  ? (highContrast ? 'text-yellow-400 underline decoration-2' : 'text-primary') 
                  : (highContrast ? 'text-white hover:text-yellow-400 focus:ring-yellow-400' : 'text-secondary hover:text-primary focus:ring-primary/20')
              }`}
              aria-current={currentPage === item.page ? 'page' : undefined}
            >
              {item.name}
            </button>
          ))}
          
          <div className="flex items-center pl-4 border-l border-gray-100 gap-4">
            <div className="relative">
              {/* <button 
                onClick={() => setShowAccess(!showAccess)}
                className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-all ${
                  highContrast 
                    ? 'border-yellow-400 bg-black text-yellow-400' 
                    : 'border-gray-200 text-secondary hover:bg-gray-50'
                } focus:ring-4 ${highContrast ? 'focus:ring-yellow-400' : 'focus:ring-primary/20'}`}
                aria-label="Barrierefreiheit-Einstellungen"
                aria-haspopup="true"
                aria-expanded={showAccess}
              >
                <span className="text-xl font-bold" aria-hidden="true">♿</span>
              </button> */}

              {/* {showAccess && (
                <div 
                  className={`absolute right-0 top-full mt-2 w-64 p-5 rounded-2xl shadow-2xl border-2 animate-fade-in ${highContrast ? 'bg-black border-yellow-400 text-white' : 'bg-white border-gray-100 text-secondary'}`}
                  role="region"
                  aria-label="Einstellungen für Barrierefreiheit"
                >
                  <p className={`text-[10px] font-bold uppercase mb-4 tracking-widest ${highContrast ? 'text-yellow-400' : 'text-gray-400'}`}>Barrierefreiheit</p>
                  <button 
                    onClick={() => setHighContrast(!highContrast)}
                    className={`w-full text-left py-3 px-4 rounded-xl mb-3 flex justify-between items-center transition-colors border-2 ${
                      highContrast 
                        ? 'bg-zinc-900 border-yellow-400 text-yellow-400' 
                        : 'bg-gray-50 border-transparent hover:bg-gray-100 text-secondary'
                    } focus:ring-2 focus:ring-primary`}
                    role="switch"
                    aria-checked={highContrast}
                  >
                    <span className="font-bold text-sm">Hoher Kontrast</span>
                    <span className={`w-8 h-4 rounded-full relative transition-colors ${highContrast ? 'bg-yellow-400' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${highContrast ? 'right-0.5' : 'left-0.5'}`}></span>
                    </span>
                  </button>
                  <div className={`p-4 rounded-xl border-2 ${highContrast ? 'bg-zinc-900 border-white' : 'bg-gray-50 border-transparent'}`}>
                    <p className={`text-[10px] font-bold uppercase mb-3 tracking-widest ${highContrast ? 'text-yellow-400' : 'text-gray-400'}`} id="font-size-label">Schriftgröße</p>
                    <div className="flex items-center justify-between" aria-labelledby="font-size-label">
                      <button 
                        onClick={() => setFontSize(Math.max(80, fontSize - 10))} 
                        className={`w-8 h-8 flex items-center justify-center border-2 rounded-lg font-bold ${highContrast ? 'border-white text-white' : 'hover:bg-white'}`}
                        aria-label="Schrift verkleinern"
                      >
                        -
                      </button>
                      <span className={`font-bold text-sm ${highContrast ? 'text-yellow-400' : ''}`} aria-live="polite">{fontSize}%</span>
                      <button 
                        onClick={() => setFontSize(Math.min(150, fontSize + 10))} 
                        className={`w-8 h-8 flex items-center justify-center border-2 rounded-lg font-bold ${highContrast ? 'border-white text-white' : 'hover:bg-white'}`}
                        aria-label="Schrift vergrößern"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )} */}
            </div>

            <button 
              onClick={() => handleNav('warteliste')} 
              onMouseEnter={() => prefetchPage('warteliste')} // PERFORMANCE: Prefetch on hover
              className={`px-6 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95 text-sm focus:ring-4 ${
                highContrast 
                  ? 'bg-yellow-400 text-black border-2 border-black hover:bg-white' 
                  : 'bg-primary text-white hover:bg-primary/90 focus:ring-primary/30'
              }`}
            >
              Warteliste beitreten
            </button>
          </div>
        </nav>

        <button 
          className={`lg:hidden focus:ring-4 p-2 rounded-lg border-2 ${highContrast ? 'text-yellow-400 border-yellow-400 focus:ring-yellow-400' : 'text-secondary border-transparent focus:ring-primary/20'}`} 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"} />
          </svg>
        </button>
      </div>

      {mobileMenuOpen && (
        <div 
          id="mobile-menu"
          className={`lg:hidden border-t-2 py-6 px-6 flex flex-col space-y-4 shadow-xl animate-fade-in ${highContrast ? 'bg-black border-yellow-400 text-white' : 'bg-white border-gray-100 text-secondary'}`}
        >
          {navItems.map((item) => (
            <button 
              key={item.name} 
              onClick={() => handleNav(item.page)} 
              className={`text-left text-lg font-bold py-2 border-b ${
                currentPage === item.page 
                  ? (highContrast ? 'text-yellow-400 border-yellow-400' : 'text-primary border-primary/20') 
                  : (highContrast ? 'text-white border-zinc-800' : 'text-secondary border-gray-50')
              }`}
              aria-current={currentPage === item.page ? 'page' : undefined}
            >
              {item.name}
            </button>
          ))}
          <div className="pt-4 border-t-2 flex flex-col space-y-4">
             <button 
              onClick={() => setHighContrast(!highContrast)} 
              className={`text-left font-bold text-sm flex justify-between items-center p-2 rounded ${highContrast ? 'bg-zinc-900 text-yellow-400' : 'bg-gray-50 text-secondary'}`}
              role="switch"
              aria-checked={highContrast}
             >
                <span>Kontrast-Modus</span>
                <span>{highContrast ? 'AKTIVIERT' : 'DEAKTIVIERT'}</span>
             </button>
             <button 
               onClick={() => handleNav('warteliste')} 
               className={`text-center py-4 rounded-xl font-bold text-lg border-2 ${
                 highContrast 
                   ? 'bg-yellow-400 text-black border-black' 
                   : 'bg-primary text-white border-transparent'
               }`}
             >
               Warteliste beitreten
             </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
